import { ExclamationTriangleIcon, EyeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createEmptyObjectDefinitionSpec, parseObjectDefinitionSpec, type ObjectDefinitionSpec } from 'shared-types';
import { Button } from '../../components/Button';
import { EarthLoader } from '../../components/loaders/EarthLoader';
import { ConfirmDialog } from '../../components/Modal';
import { Canvas2D } from '../../modules/objectEditor/canvas/Canvas2D';
import { EditorStore } from '../../modules/objectEditor/document/EditorStore';
import { EditorHeader } from '../../modules/objectEditor/EditorHeader';
import { EditorShortcuts, EditorToolSettings, TOOL_HINTS } from '../../modules/objectEditor/EditorToolbar';
import { EditorLeftPanel, EditorRightPanel, EditorViewSwitch } from '../../modules/objectEditor/layout/EditorPanels';
import { HintBar } from '../../components/HintBar';
import { ObjectEditorContext, type EditorUi, type ObjectEditorContextValue } from '../../modules/objectEditor/EditorContext';
import { ImportDialog } from '../../modules/objectEditor/import/ImportDialog';
import { ProblemsBar } from '../../modules/objectEditor/ProblemsBar';
import { useAutosave } from '../../modules/objectEditor/useAutosave';
import { useEditorShortcuts } from '../../modules/objectEditor/useEditorShortcuts';
import { ModelView } from '../../modules/objectEditor/view3d/ModelView';
import { libraryCategoryOptions } from '../../modules/objectLibrary/categories';
import { T, useT, useTranslator } from '../../translation/src';
import {
  useObjectDefinitionCreateMutation,
  useObjectDefinitionDiscardDraftMutation,
  useObjectDefinitionPublishMutation,
  useObjectDefinitionQuery,
  useOrganisationObjectLibraryQuery,
  type ObjectDefinitionFieldsFragment,
} from '../../types/graphqlTypes';

/** A smart object only has Test mode: the AI changes the drawing. */
const initialUi = (smart: boolean): EditorUi => ({
  tool: 'select',
  grid: 0.01,
  snapToPoints: true,
  mode: smart ? 'test' : 'edit',
  view: '2d',
  test: { params: {} },
  preview: null,
  node: null,
  editModel: false,
});

const LoadedEditor: React.FC<{ organisationId: string; definition: ObjectDefinitionFieldsFragment; initialSpec: ObjectDefinitionSpec; categories: ObjectEditorContextValue['categories'] }> = ({
  organisationId,
  definition,
  initialSpec,
  categories,
}) => {
  const navigate = useNavigate();
  const smart = definition.smart;
  const [store] = useState(() => new EditorStore(initialSpec));
  const [ui, setUiState] = useState<EditorUi>(() => initialUi(smart));
  const [error, setError] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmSmartCopy, setConfirmSmartCopy] = useState(false);
  // The History tab lists the new version right away.
  const [publish, { loading: publishing }] = useObjectDefinitionPublishMutation({ refetchQueries: ['objectDefinitionVersions'] });
  const [discard] = useObjectDefinitionDiscardDraftMutation();
  const [createDefinition] = useObjectDefinitionCreateMutation();
  const { status, issues, saveNow, markSaved } = useAutosave({ store, organisationId, definitionId: definition.id });

  const messages = {
    saveFailed: useT('the draft could not be saved', { swc: true }),
    publishFailed: useT('publishing failed', { swc: true }),
    published: useT('published: projects now use the new version', { swc: true }),
    discarded: useT('changes discarded', { swc: true }),
    groupName: useT('group', { swc: true }),
    dismiss: useT('dismiss', { swc: true }),
    missingAsset: useT('the 3D model is missing: upload it again', { swc: true }),
    invalidSpec: useT('the drawing has problems: fix them first', { swc: true }),
    smartCopyFailed: useT('the smart copy could not be made', { swc: true }),
    copySuffix: useT('copy'),
  };

  // A smart object never leaves Test mode (the T shortcut included).
  const setUi = useCallback((update: (ui: EditorUi) => EditorUi) => setUiState(smart ? (u) => ({ ...update(u), mode: 'test' }) : update), [smart]);
  const translate = useTranslator();
  const hint = translate(TOOL_HINTS[ui.tool], { swc: true });
  // Doors / windows / dimensions have no 3D model: 2D only (an object can't become one while editing — the server keeps the kind).
  const planOnly = !!initialSpec.opening || !!initialSpec.dimension;
  const view = planOnly ? '2d' : ui.view;
  const onSave = useCallback(() => {
    void saveNow().then((ok) => setError(ok ? null : messages.saveFailed));
  }, [saveNow, messages.saveFailed]);
  useEditorShortcuts({ store, ui, setUi }, { onSave, groupName: messages.groupName });

  const doPublish = async () => {
    setError(null);
    if (!(await saveNow())) {
      setError(messages.saveFailed);
      return;
    }
    try {
      await publish({ variables: { organisationId, definitionId: definition.id } });
      toast.success(messages.published);
    } catch (e) {
      const code = e instanceof Error ? e.message : '';
      setError(code === 'MISSING_ASSET' ? messages.missingAsset : code === 'INVALID_SPEC' ? messages.invalidSpec : messages.publishFailed);
    }
  };

  /** The current drawing (unsaved edits included) as a new smart object, opened in its own editor. */
  const doSmartCopy = async () => {
    setError(null);
    try {
      const result = await createDefinition({
        variables: { organisationId, name: `${definition.name} ${messages.copySuffix}`.slice(0, 80), categoryId: definition.categoryId, spec: store.getState().spec, duplicateOfDefinitionId: definition.id, smart: true },
      });
      const id = result.data?.objectDefinitionCreate.id;
      if (id) navigate(`/organisations/${organisationId}/object-library/${id}/edit`);
    } catch (e) {
      setError(e instanceof Error && e.message === 'INVALID_SPEC' ? messages.invalidSpec : messages.smartCopyFailed);
    }
  };

  const resetDraft = useCallback(
    (spec: ObjectDefinitionSpec) => {
      store.reset(spec);
      markSaved();
      setUiState((u) => ({ ...u, preview: null }));
    },
    [store, markSaved],
  );
  const doDiscard = async () => {
    const result = await discard({ variables: { organisationId, definitionId: definition.id } });
    const parsed = parseObjectDefinitionSpec(result.data?.objectDefinitionDiscardDraft.draftSpec);
    if (parsed.ok) {
      resetDraft(parsed.spec);
      toast.success(messages.discarded);
    }
  };

  const context = useMemo<ObjectEditorContextValue>(() => ({ store, organisationId, definition, categories, ui, setUi, resetDraft }), [store, organisationId, definition, categories, ui, setUi, resetDraft]);

  const canvas = (
    <div className="relative h-full w-full">
      <Canvas2D />
      <ProblemsBar issues={issues} />
    </div>
  );

  return (
    <ObjectEditorContext.Provider value={context}>
      <div className="flex h-screen w-full flex-col bg-white text-black dark:bg-[#313131] dark:text-white">
        <EditorHeader
          saveStatus={status}
          issueCount={issues.length}
          onBack={() => navigate(`/organisations/${organisationId}/object-library`)}
          onPublish={() => void doPublish()}
          onDiscard={() => setConfirmDiscard(true)}
          onImport={() => setImportOpen(true)}
          onMakeSmart={() => setConfirmSmartCopy(true)}
          publishing={publishing}
        />
        <div className="flex min-h-0 flex-1">
          <EditorLeftPanel />
          <main className="relative flex min-w-0 flex-1 flex-col">
            {/* The project's tools strip: the active tab's tool row sits on it; settings at the right. */}
            <div data-panel="tools-bar" className="z-[5] flex h-[52px] flex-shrink-0 items-center justify-end bg-[rgb(210,210,210)] pr-4 dark:bg-[rgb(45,45,45)]">
              {!smart && <EditorToolSettings />}
            </div>
            {error && (
              <div role="alert" className="flex items-center gap-3 border-b border-error-red-500/40 bg-error-red-500/10 px-4 py-2 text-sm">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-error-red-500" />
                <span className="flex-1">{error}</span>
                <button type="button" aria-label={messages.dismiss} onClick={() => setError(null)}>
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            )}
            {ui.preview && (
              <div className="flex items-center gap-3 border-b border-sky-500/40 bg-sky-500/10 px-4 py-2 text-sm">
                <EyeIcon className="h-5 w-5 shrink-0 text-sky-600 dark:text-sky-400" />
                <span className="flex-1">
                  <T _str="previewing published version {version} (read-only)" args={{ version: ui.preview.version }} swc />
                </span>
                <Button variant="secondary" size="xs" onClick={() => setUi((u) => ({ ...u, preview: null }))}>
                  <T _str="back to the draft" swc />
                </Button>
              </div>
            )}
            <div className="relative min-h-0 flex-1">
              {view === '2d' && canvas}
              {view === '3d' && <ModelView />}
              {view === 'split' && (
                <div className="grid h-full grid-cols-2">
                  <div className="min-w-0 border-r border-gray-300 dark:border-gray-700">{canvas}</div>
                  <div className="min-w-0">
                    <ModelView />
                  </div>
                </div>
              )}
              <HintBar text={ui.mode === 'edit' && !ui.preview ? hint : ''} className="absolute left-0 right-0 top-2 z-[4]" />
              {!planOnly && <EditorViewSwitch className="absolute bottom-4 left-4" />}
              {!smart && <EditorShortcuts className="absolute bottom-4 right-4" />}
            </div>
          </main>
          <EditorRightPanel />
        </div>
      </div>
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onError={setError} />
      <ConfirmDialog
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={doDiscard}
        variant="warning"
        title={<T _str="discard unpublished changes?" swc />}
        message={<T _str="the drawing goes back to the published version; this can't be undone" swc />}
        confirmLabel={<T _str="discard" swc />}
      />
      <ConfirmDialog
        open={confirmSmartCopy}
        onClose={() => setConfirmSmartCopy(false)}
        onConfirm={doSmartCopy}
        variant="info"
        title={<T _str="make a smart copy?" swc />}
        message={<T _str="a copy of this object opens as a smart object. You change it by describing what you want. This object stays as it is." swc />}
        confirmLabel={<T _str="make a smart copy" swc />}
      />
    </ObjectEditorContext.Provider>
  );
};

/**
 * The Object Editor page: loads the definition and the organisation's
 * categories, then hands a document store seeded with the draft to the
 * editor. A draft the validator rejects (hand-edited data, older format)
 * gets a way out instead of a broken editor.
 */
export const ObjectEditor: React.FC2 = () => {
  const { organisationId = '', definitionId = '' } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useObjectDefinitionQuery({ variables: { organisationId, definitionId }, fetchPolicy: 'network-only' });
  const { data: libraryData } = useOrganisationObjectLibraryQuery({ variables: { organisationId }, fetchPolicy: 'cache-first' });
  const definition = data?.objectDefinition;
  /** The user chose to replace a damaged draft with an empty drawing. */
  const [startEmpty, setStartEmpty] = useState(false);
  const categories = useMemo(() => libraryCategoryOptions(libraryData?.organisationObjectLibrary.config), [libraryData]);
  // The editor reads its starting spec once (at mount); refetches after saves only refresh the header.
  const parsed = useMemo(() => (definition ? parseObjectDefinitionSpec(definition.draftSpec ?? definition.publishedSpec) : null), [definition]);

  if (loading && !definition) return <EarthLoader />;
  const backToLibrary = () => navigate(`/organisations/${organisationId}/object-library`);
  if (error || !definition || !parsed)
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p>
          <T _str="this object could not be opened" swc />
        </p>
        <Button variant="secondary" size="xs" onClick={backToLibrary}>
          <T _str="back to the object library" swc />
        </Button>
      </div>
    );
  if (!parsed.ok && !startEmpty)
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 p-8">
        <p className="font-medium">
          <T _str="the saved drawing of this object is damaged" swc />
        </p>
        <ul className="max-w-xl text-sm text-gray-500">
          {parsed.issues.slice(0, 5).map((issue, i) => (
            <li key={i}>
              {issue.path}: {issue.message}
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <Button variant="secondary" size="xs" onClick={backToLibrary}>
            <T _str="back to the object library" swc />
          </Button>
          {/* The first autosave of the empty drawing replaces the damaged draft. */}
          <Button variant="primary" size="xs" onClick={() => setStartEmpty(true)}>
            <T _str="start from an empty drawing" swc />
          </Button>
        </div>
      </div>
    );

  return <LoadedEditor key={definition.id} organisationId={organisationId} definition={definition} initialSpec={parsed.ok ? parsed.spec : createEmptyObjectDefinitionSpec()} categories={categories} />;
};
